import { Modal } from "../Modal";
import React, { useState, useEffect } from "react";
import styles from "./styles.module.css";
import { Button } from "src/components/atoms/Button";
import { useTheme } from "src/context/ThemeContext";
import { Msg } from "src/i18n/Msg";
import cx from "classnames";

import chooseDesktop from "src/assets/images/choose_desktop.webp";
import doDesktop from "src/assets/images/do_desktop.webp";
import getDesktop from "src/assets/images/get_desktop.webp";

import chooseBlack from "src/assets/images/choose_black.webp";
import doBlack from "src/assets/images/do_black.webp";
import getBlack from "src/assets/images/get_black.webp";
import chooseBlackMobile from "src/assets/images/choose_black_mobile.webp";
import doBlackMobile from "src/assets/images/do_black_mobile.webp";
import getBlackMobile from "src/assets/images/get_black_mobile.webp";

import AboutNext from "src/assets/icons/about_next.svg";

const MOBILE_BREAKPOINT = 723;

type Props = {
  isOpen: boolean;
  close: () => void;
};

export const OnboardingModal: React.FC<Props> = ({ isOpen, close }) => {
  const [step, setStep] = useState(1);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [isMobileModal, setIsMobileModal] = useState(
    () => window.innerWidth <= MOBILE_BREAKPOINT,
  );

  useEffect(() => {
    const handler = () =>
      setIsMobileModal(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    setStep(1);
    close();
  };

  const nextButton = isMobileModal ? (
    <button
      onClick={handleNext}
      className={styles.nextButton}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        src={AboutNext}
        alt="Next"
        style={{ width: "21px", height: "16px" }}
      />
    </button>
  ) : (
    <Button
      onClick={handleNext}
      className={styles.nextButton}
      text={{
        id:
          step < 3
            ? "components.organisms.OnboardingModal.next"
            : "components.organisms.OnboardingModal.next",
      }}
    />
  );

  // On mobile always use black images; on desktop use theme-dependent images
  const img1 = isMobileModal
    ? chooseBlackMobile
    : isDark
      ? chooseBlack
      : chooseDesktop;
  const img2 = isMobileModal ? doBlackMobile : isDark ? doBlack : doDesktop;
  const img3 = isMobileModal ? getBlackMobile : isDark ? getBlack : getDesktop;

  // Предзагрузка картинок всех шагов при открытии — чтобы переход «Далее»
  // не ждал скачивания следующей картинки.
  useEffect(() => {
    if (!isOpen) return;
    [img1, img2, img3].forEach((src) => {
      const preload = new Image();
      preload.src = src;
    });
  }, [isOpen, img1, img2, img3]);

  return (
    <Modal
      isVisible={isOpen}
      closeModal={handleClose}
      className={styles.onboardingModal}
      closeIconMode={isMobileModal ? "white" : "black"}
    >
      <div className={cx(styles.content, step === 3 && styles.content3)}>
        {step === 1 && (
          <div className={cx(styles.step, styles.step1)}>
            <img
              className={styles.img1}
              src={img1}
              alt="Choose an exchange"
              decoding="async"
            />
            <div className={styles.textBlock}>
              <h3 className={styles.header}>
                <Msg id="components.organisms.OnboardingModal.step1.title" />
              </h3>
              <p className={styles.text}>
                <Msg id="components.organisms.OnboardingModal.step1.text" />
              </p>
              {nextButton}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={cx(styles.step, styles.step2)}>
            <img
              className={styles.img2}
              src={img2}
              alt="Do a forecast"
              decoding="async"
            />
            <div className={styles.textBlock}>
              <h3 className={styles.header2}>
                <Msg id="components.organisms.OnboardingModal.step2.title" />
              </h3>
              <p className={styles.text2}>
                <Msg id="components.organisms.OnboardingModal.step2.line1" />
                <br />
                <Msg id="components.organisms.OnboardingModal.step2.line2" />
                <br />
                <Msg id="components.organisms.OnboardingModal.step2.line3" />
                <br />
                <Msg id="components.organisms.OnboardingModal.step2.line4" />
              </p>
              {nextButton}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className={styles.step3}>
            <img
              className={styles.img3}
              src={img3}
              alt="Get profit"
              decoding="async"
            />
            <div className={styles.textBlock}>
              <h3 className={styles.header}>
                <Msg id="components.organisms.OnboardingModal.step3.title" />
              </h3>
              <p className={styles.text3}>
                <Msg id="components.organisms.OnboardingModal.step3.text" />
              </p>
              {nextButton}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
